import { useRef, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideHover } from "./useTourGuideHover";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { TourGuideType } from "@root/constants/tour-guide";

/**
 * Tour Guide Hook for Unauthenticated Dashboard
 *
 * Guides users through the main features of the unauthenticated dashboard:
 * - Sidebar navigation
 * - Header section
 * - Course recommendations
 */
export const useUnauthenticatedDashboardTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();

  // Create refs for elements that need them
  const courseCardRef = useRef<HTMLDivElement>(null);

  // Detect if we're on the unauthenticated dashboard route
  const isOnUnauthenticatedDashboard = pathname === "/";

  // Define tour configuration
  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "unauthenticatedDashboard",
      enabled: isOnUnauthenticatedDashboard,
      steps: [
        // Sidebar Steps
        {
          id: "sidebar-home",
          targetSelector: '[data-tour-item="sidebar-menu-0"]',
          title: t("tourGuide.unauthenticatedDashboard.step1.title"),
          description: t(
            "tourGuide.unauthenticatedDashboard.step1.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
        },
        {
          id: "sidebar-courses",
          targetSelector: '[data-tour-item="sidebar-menu-1"]',
          title: t("tourGuide.unauthenticatedDashboard.step2.title"),
          description: t(
            "tourGuide.unauthenticatedDashboard.step2.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
        },
        {
          id: "sidebar-language",
          targetSelector: '[data-tour-item="sidebar-language-toggle"]',
          title: t("tourGuide.unauthenticatedDashboard.step3.title"),
          description: t(
            "tourGuide.unauthenticatedDashboard.step3.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 10, // Better fit for language toggler
        },
        // Header Steps - Pledge Login
        {
          id: "header-pledge-login",
          targetSelector: '[data-tour-item="header-pledge-login"]',
          title: t("tourGuide.unauthenticatedDashboard.step4.title"),
          description: t(
            "tourGuide.unauthenticatedDashboard.step4.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
        },
        // Header Steps - Sign Up
        {
          id: "header-signup",
          targetSelector: '[data-tour-item="header-signup"]',
          title: t("tourGuide.unauthenticatedDashboard.step5.title"),
          description: t(
            "tourGuide.unauthenticatedDashboard.step5.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
        },
        // Header Steps - Journalist Login
        {
          id: "header-journalist-login",
          targetSelector: '[data-tour-item="header-journalist-login"]',
          title: t("tourGuide.unauthenticatedDashboard.step6.title"),
          description: t(
            "tourGuide.unauthenticatedDashboard.step6.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
        },
        // Course Card Step
        {
          id: "course-card",
          targetRef: courseCardRef,
          title: t("tourGuide.unauthenticatedDashboard.step7.title"),
          description: t(
            "tourGuide.unauthenticatedDashboard.step7.description",
          ),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 12, // Extra padding for better fit around course card
        },
        // View All Button Step
        {
          id: "view-all-btn",
          targetSelector: '[data-tour-item="view-all-button"]',
          title: t("tourGuide.unauthenticatedDashboard.step8.title"),
          description: t(
            "tourGuide.unauthenticatedDashboard.step8.description",
          ),
          position: "left",
          offset: { x: -15, y: 0 },
          highlightPadding: 10, // Better fit for view all button
        },
      ],
    }),
    [isOnUnauthenticatedDashboard, t],
  );

  // Create base tour guide
  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.UnauthenticatedDashboard, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const unauthenticatedDashboardTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.UnauthenticatedDashboard,
    isOnTourPath: isOnUnauthenticatedDashboard,
    onTourComplete: handleTourComplete,
  });

  // Sync with tour guide context
  useEffect(() => {
    if (unauthenticatedDashboardTourGuide.isActive) {
      setActiveTourModule("unauthenticatedDashboard");
      setCurrentTourStep(unauthenticatedDashboardTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    unauthenticatedDashboardTourGuide.isActive,
    unauthenticatedDashboardTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  // Create hover tooltip instances for each step
  const sidebarHomeHover = useTourGuideHover("sidebar-home", config.steps[0]);
  const sidebarCoursesHover = useTourGuideHover(
    "sidebar-courses",
    config.steps[1],
  );
  const sidebarLanguageHover = useTourGuideHover(
    "sidebar-language",
    config.steps[2],
  );
  const headerPledgeLoginHover = useTourGuideHover(
    "header-pledge-login",
    config.steps[3],
  );
  const headerSignupHover = useTourGuideHover("header-signup", config.steps[4]);
  const headerJournalistLoginHover = useTourGuideHover(
    "header-journalist-login",
    config.steps[5],
  );
  const courseCardHover = useTourGuideHover("course-card", config.steps[6]);
  const viewAllBtnHover = useTourGuideHover("view-all-btn", config.steps[7]);

  return {
    unauthenticatedDashboardTourGuide,
    // Refs to attach to elements
    courseCardRef,
    // Hover tooltip instances
    sidebarHomeHover,
    sidebarCoursesHover,
    sidebarLanguageHover,
    headerPledgeLoginHover,
    headerSignupHover,
    headerJournalistLoginHover,
    courseCardHover,
    viewAllBtnHover,
  };
};
